// src/components/appSettings/Banner/BannerForm/BannerForm.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  createBannerThunk,
  updateBannerThunk,
  fetchBannerByIdThunk,
} from "../../../../store/thunks/appSettingsThunks";
import { useBannerForm } from "./bannerFormHooks";
import Button from "../../../../components/common/Button";
import toast from "react-hot-toast";
import {
  BannerBasicInfoSection,
  BannerMediaSection,
  BannerActionSection,
  BannerTargetingSection,
  BannerDisplayRulesSection,
  BannerSchedulingSection,
} from "./BannerFormSections";

const BannerForm = ({ mode = "add" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedBanner, loading } = useSelector((state) => state.appSettings);
  const { user } = useSelector((state) => state.auth);
  const { branches } = useSelector((state) => state.branch);

  // Use custom form hook
  const {
    formData,
    errors,
    submitting,
    imageFiles,
    imagePreviews,
    handleChange,
    handleImageUpload,
    handleImageRemove,
    handleSubmit,
    validateForm,
  } = useBannerForm(selectedBanner, mode);

  // Load banner data for edit mode
  useEffect(() => {
    if (mode === "edit" && id) {
      dispatch(fetchBannerByIdThunk(id));
    }
  }, [mode, id, dispatch]);

  // Handle form submission
  const onSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    await handleSubmit();
  };

  if (mode === "edit" && loading && !selectedBanner) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6 pb-20">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === "add" ? "Create New Banner" : "Edit Banner"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {mode === "add"
                ? "Add a new promotional banner to your app"
                : `Editing: ${selectedBanner?.title || "Banner"}`}
            </p>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <BannerBasicInfoSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Media Upload */}
      <BannerMediaSection
        formData={formData}
        imageFiles={imageFiles}
        imagePreviews={imagePreviews}
        errors={errors}
        onImageUpload={handleImageUpload}
        onImageRemove={handleImageRemove}
        mode={mode}
      />

      {/* Action Configuration */}
      <BannerActionSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Targeting */}
      <BannerTargetingSection
        formData={formData}
        errors={errors}
        branches={branches}
        onChange={handleChange}
      />

      {/* Display Rules */}
      <BannerDisplayRulesSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Scheduling */}
      <BannerSchedulingSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Form Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-10">
        <div className="container mx-auto flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/app-settings/banners")}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={submitting} disabled={submitting}>
            {submitting
              ? "Saving..."
              : mode === "add"
              ? "Create Banner"
              : "Update Banner"}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default BannerForm;
